// src/app/shared/interceptors/auth.interceptor.ts
import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, switchMap, tap, catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SKIP_AUTH } from '../tokens/http-context.tokens';
import { MatDialog } from '@angular/material/dialog';
import { SingleActionDialogComponent } from '../components/dialogs/single-action-dialog/single-action-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ConfigLoaderService } from '../services/config-loader.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // 排除 refresh sessions API，避免無限循環
    if (req.url.includes('/member/refreshsessions') || req.context.get(SKIP_AUTH)) {
      return next.handle(req);
    }
    
    const auth = this.injector.get(AuthService);
    const configLoader = this.injector.get(ConfigLoaderService);
    
    // 如果 config 還沒載入完成，直接放行
    if (!configLoader.getConfig()) {
      return next.handle(req);
    }

    // 取得目前 token
    const token = auth.token;

    if (!token) {
      return next.handle(req); // 未登入，照常送出
    }

    // token 還有效，直接使用現有 token
    const authReq = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });

    // tap監測每個回應
    return next.handle(authReq).pipe(
      tap(response => this.checkForDuplicateLogin(response))
    );
  }

  // 檢驗是否有異地登入
  private checkForDuplicateLogin(response: any): void {
    if (response?.body?.statusCode === 'EC6010') {
      this.handleDuplicateLogin();
    }
  }

  // 處理異地登入
  private handleDuplicateLogin(): void {
    const dialog = this.injector.get(MatDialog);
    const translateService = this.injector.get(TranslateService);
    const authService = this.injector.get(AuthService);
    const router = this.injector.get(Router);

    dialog.open(SingleActionDialogComponent, {
      disableClose: true,
      data: {
        title: translateService.instant('id-login.logouted'),
        message: translateService.instant('id-login.logouted-content'),
        showCloseButton: false,
        buttonText: translateService.instant('common.button.i-understood'),
        action: () => {
          authService.performLogout().subscribe({
            next: (res) => {
              authService.clearToken();
              console.log('登出 API 成功:', res);
            },
            error: (error) => {
              authService.clearToken();
              console.error('登出 API 失敗:', error);
            }
          });
          router.navigate([''], { replaceUrl: true });
          dialog.closeAll();
        },
      },
    });
  }
}
